import { useQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Category as VccCategory, run } from "vanilla-cookieconsent";

import { COOKIE_CATEGORIES_QUERY } from "core/includes/queries";
import { ON_COOKIE_CONSENT_EVENT_NAME, removeUnconsentedCookies } from "core/includes/cookies";
import { CookieCategory, VccSection } from "core/types/cookies";
import window from 'core/includes/window';

type CookieContextObject = {
    consentedCategories: CookieCategory[];
};

type CookieContextProviderProps = {
    children: ReactNode;
    isConsentingEnabled?: boolean;
};

const CookieContext = createContext<CookieContextObject>({
    consentedCategories: [],
});

export const CookieContextProvider = ({ children, isConsentingEnabled = false }: CookieContextProviderProps) => {
    const [consentedCategories, setConsentedCategories] = useState<CookieCategory[]>([]);

    const { data: categoriesData } = useQuery(
        COOKIE_CATEGORIES_QUERY,
        { skip: !isConsentingEnabled }
    );
    const categories: CookieCategory[] = useMemo(
        () => categoriesData?.cookieCategories ?? [],
        [categoriesData],
    );

    /**
     * Helper function for determining the VCC category name after passing our CookieCategory.
     */
    const getVccCategoryName = (category: CookieCategory) => {
        return `${category.id}-${category.name}`;
    }

    const {
        vccCategories,
        vccSections,
    } = useMemo(() => {
        const output: {
            vccCategories: {[key: string]: VccCategory};
            vccSections: VccSection[];
        } = {
            vccCategories: {},
            vccSections: [],
        };

        if (!categories) return output;

        for (const category of categories) {
            if (!category.name || !category.id) continue;

            const cookies  = category.cookies ?? [];

            // The internal VCC category name uses the ID and Name of the CookieCategory to prevent same-name conflicts
            const vccCategoryName = getVccCategoryName(category);

            // Create the VCC category
            output.vccCategories[vccCategoryName] = {
                enabled: true,
                readOnly: !category.isManageable,
                autoClear: {
                    cookies: cookies.map(cookie => ({
                        name: cookie.name || "",
                        path: "/",
                        domain: cookie.domain || "",
                    })),
                    reloadPage: true,
                },
            };

            // Create a VCC form section for the category
            output.vccSections.push({
                title: category.name,
                linkedCategory: vccCategoryName,
                description: category.description || "",
                cookieTable: {
                    caption: "Cookies",
                    headers: {
                        name: "Cookie",
                        duration: "Duration",
                        domain: "Domain",
                        desc: "Description",
                    },
                    body: cookies.map(cookie => ({
                        name: cookie.name || "",
                        duration: cookie.duration || "",
                        domain: cookie.domain || "",
                        desc: cookie.description || "",
                    })),
                }
            });
        }

        return output;
    }, [categories]);

    useEffect(function initialiseCookieConsenting() {
        if (isEmpty(vccCategories) || isEmpty(vccSections) || !isConsentingEnabled) return;

        run({
            onConsent: ({ cookie: vccData }) => {
                const event = new Event(ON_COOKIE_CONSENT_EVENT_NAME);
                window.dispatchEvent(event);
                const consentedCategoryNames = vccData.categories ?? [];
                setConsentedCategories(categories.filter(
                    category => (
                        category.name &&
                        consentedCategoryNames.includes(getVccCategoryName(category))
                    )
                ));
                removeUnconsentedCookies();
            },
            categories: vccCategories,
            language: {
                // TODO WRT-17819: We should resolve other translation configs
                default: "en",
                translations: {
                    en: {
                        consentModal: {
                            title: "We use cookies",
                            description: "This website uses cookies to improve your experience. Click ‘Accept' if " +
                                "that’s okay. Alternatively, you can customise your cookie preferences or " +
                                "opt-out if you wish.",
                            acceptAllBtn: "Accept",
                            acceptNecessaryBtn: "Reject",
                            showPreferencesBtn: "Manage Cookie Settings"
                        },
                        preferencesModal: {
                            title: "Overview",
                            acceptAllBtn: "Accept all",
                            acceptNecessaryBtn: "Reject all",
                            savePreferencesBtn: "Accept current selection",
                            closeIconLabel: "Close modal",
                            sections: [
                                {
                                    description: "We use cookies to give you the best experience while you navigate " +
                                        "our website. The cookies categorised as 'necessary' are stored on your " +
                                        "browser, as they are essential for basic functionalities of the website." +
                                        " We also use third-party cookies that help us analyse and understand how" +
                                        "you use the website. These cookies will only be stored in your browser with" +
                                        " your consent. You also have the option to opt-out of these cookies; " +
                                        "however, opting out of some of these cookies may effect your browsing " +
                                        "experience."
                                },
                                ...vccSections,
                                {
                                    title: "More information",
                                    description: 'For any queries in relation to my policy on cookies and your ' +
                                        'choices, please <a href="/about/contact-us/">contact us</a>.'
                                }
                            ]
                        }
                    }
                }
            }
        }).then(() => {
            // Dynamically apply styles to the consent modal
            const consentContainer = document.getElementById('cc-main');
            if (consentContainer) {
                consentContainer.style.setProperty('--cc-btn-primary-bg', '#1D8BDB');
                consentContainer.style.setProperty('--cc-btn-primary-border-color', '#1D8BDB');
                consentContainer.style.setProperty('--cc-btn-primary-hover-bg', '#68BAF9');
                consentContainer.style.setProperty('--cc-btn-primary-hover-border-color', '#68BAF9');
            }
        });
    }, [categories, vccCategories, vccSections, isConsentingEnabled]);

    return (
        <CookieContext.Provider
            value={{
                consentedCategories: consentedCategories,
            }}
        >
            {children}
        </CookieContext.Provider>
    );
};

export const useCookieContext = () => useContext(CookieContext);